import type { UpgradeCategory, UpgradeDef, UpgradeStats} from './UpgradeData';
import { getUpgradeById } from './UpgradeData';
import type { WeaponDef} from './WeaponData';
import { getWeaponById } from './WeaponData';
import { type ScoredItem, scoreMult } from './Quality';

export interface EquippedSlots {
  engine: ScoredItem | null;
  sensors: ScoredItem | null;
  capacitor: ScoredItem | null;
  shield: ScoredItem | null;
  chassis: ScoredItem | null;
}

export class UpgradeSystem {
  private slots: EquippedSlots = {
    engine: null, sensors: null, capacitor: null, shield: null, chassis: null,
  };

  private _inventory: ScoredItem[] = [];
  private _weaponInventory: ScoredItem[] = [];
  private _equippedWeapons: (ScoredItem | null)[] = [null];

  /** Called by `equip`/`unequip` after any slot mutation. Used by the
   *  achievement system to recompute equip-slot counters. Initially unset. */
  onEquipChange: ((equipped: (ScoredItem | null)[]) => void) | null = null;

  get inventory(): readonly ScoredItem[] { return this._inventory; }
  get weaponInventory(): readonly ScoredItem[] { return this._weaponInventory; }

  /** Snapshot of all 5 upgrade slots in canonical order. */
  private slotsArray(): (ScoredItem | null)[] {
    return [this.slots.engine, this.slots.sensors, this.slots.capacitor, this.slots.shield, this.slots.chassis];
  }

  private fireEquipChange(): void {
    if (this.onEquipChange) this.onEquipChange(this.slotsArray());
  }

  // ─── Upgrade equip/unequip ──────────────────────────────

  equip(item: ScoredItem): boolean {
    const def = getUpgradeById(item.id);
    if (!def) return false;

    const slot = def.category as keyof EquippedSlots;

    // Remove the item from inventory FIRST, before pushing old equipped item back.
    // Items carry generated names, so match by identity via id + score + name.
    const idx = this._inventory.findIndex(i => i.id === item.id && i.score === item.score && i.name === item.name);
    if (idx >= 0) this._inventory.splice(idx, 1);

    const current = this.slots[slot];
    if (current) this._inventory.push(current);

    this.slots[slot] = item;

    if (def.category === 'chassis') this.syncWeaponSlots();
    this.fireEquipChange();
    return true;
  }

  unequip(category: UpgradeCategory): ScoredItem | null {
    const slot = category as keyof EquippedSlots;
    const current = this.slots[slot];
    if (!current) return null;

    this._inventory.push(current);
    this.slots[slot] = null;

    if (category === 'chassis') this.syncWeaponSlots();
    this.fireEquipChange();
    return current;
  }

  getEquipped(category: UpgradeCategory): { def: UpgradeDef; item: ScoredItem } | null {
    const item = this.slots[category as keyof EquippedSlots];
    if (!item) return null;
    const def = getUpgradeById(item.id);
    if (!def) return null;
    return { def, item };
  }

  getEquippedItem(category: UpgradeCategory): ScoredItem | null {
    return this.slots[category as keyof EquippedSlots];
  }

  getAllEquipped(): ({ def: UpgradeDef; item: ScoredItem } | null)[] {
    const categories: UpgradeCategory[] = ['engine', 'sensors', 'capacitor', 'shield', 'chassis'];
    return categories.map(c => this.getEquipped(c));
  }

  addToInventory(item: ScoredItem) {
    this._inventory.push(item);
  }

  removeFromInventory(item: ScoredItem): boolean {
    const idx = this._inventory.findIndex(i => i.id === item.id && i.score === item.score && i.name === item.name);
    if (idx < 0) return false;
    this._inventory.splice(idx, 1);
    return true;
  }

  // ─── Weapon equip/unequip ──────────────────────────────

  equipWeapon(slotIndex: number, item: ScoredItem): boolean {
    if (slotIndex < 0 || slotIndex >= this._equippedWeapons.length) return false;
    const def = getWeaponById(item.id);
    if (!def) return false;

    const idx = this._weaponInventory.findIndex(i => i.id === item.id && i.score === item.score && i.name === item.name);
    if (idx >= 0) this._weaponInventory.splice(idx, 1);

    const current = this._equippedWeapons[slotIndex];
    if (current) this._weaponInventory.push(current);

    this._equippedWeapons[slotIndex] = item;
    return true;
  }

  unequipWeapon(slotIndex: number): ScoredItem | null {
    if (slotIndex < 0 || slotIndex >= this._equippedWeapons.length) return null;
    const current = this._equippedWeapons[slotIndex];
    if (!current) return null;

    this._weaponInventory.push(current);
    this._equippedWeapons[slotIndex] = null;
    return current;
  }

  getEquippedWeapon(slotIndex: number): { def: WeaponDef; item: ScoredItem } | null {
    const item = this._equippedWeapons[slotIndex];
    if (!item) return null;
    const def = getWeaponById(item.id);
    if (!def) return null;
    return { def, item };
  }

  getEquippedWeaponItem(slotIndex: number): ScoredItem | null {
    return this._equippedWeapons[slotIndex] ?? null;
  }

  getEquippedWeapons(): ({ def: WeaponDef; item: ScoredItem } | null)[] {
    return this._equippedWeapons.map((_, i) => this.getEquippedWeapon(i));
  }

  get equippedWeaponSlots(): readonly (ScoredItem | null)[] {
    return this._equippedWeapons;
  }

  addWeaponToInventory(item: ScoredItem) {
    this._weaponInventory.push(item);
  }

  removeWeaponFromInventory(item: ScoredItem): boolean {
    const idx = this._weaponInventory.findIndex(i => i.id === item.id && i.score === item.score && i.name === item.name);
    if (idx < 0) return false;
    this._weaponInventory.splice(idx, 1);
    return true;
  }

  private syncWeaponSlots() {
    const targetCount = this.weaponSlots;
    const current = this._equippedWeapons;

    if (current.length > targetCount) {
      for (let i = targetCount; i < current.length; i++) {
        if (current[i]) this._weaponInventory.push(current[i]!);
      }
      this._equippedWeapons = current.slice(0, targetCount);
    } else {
      while (this._equippedWeapons.length < targetCount) {
        this._equippedWeapons.push(null);
      }
    }
  }

  // ─── Computed stats ─────────────────────────────────────

  getComputedStats(): Required<UpgradeStats> {
    const base: Required<UpgradeStats> = {
      speedMult: 1.0, turnSpeedMult: 1.0, detectionBonus: 0, visionBonus: 0,
      maxEnergyBonus: 0, drainMult: 1.0, damageReduction: 0, weaponSlots: 1,
      maxStructureBonus: 0,
    };

    for (const eq of this.getAllEquipped()) {
      if (!eq) continue;
      const s = eq.def.stats;
      const qm = scoreMult(eq.item.score);

      if (s.speedMult !== undefined)      base.speedMult *= 1 + (s.speedMult - 1) * qm;
      if (s.turnSpeedMult !== undefined)  base.turnSpeedMult *= 1 + (s.turnSpeedMult - 1) * qm;
      if (s.detectionBonus !== undefined) base.detectionBonus += s.detectionBonus * qm;
      if (s.visionBonus !== undefined)    base.visionBonus += s.visionBonus * qm;
      if (s.maxEnergyBonus !== undefined) base.maxEnergyBonus += s.maxEnergyBonus * qm;
      if (s.drainMult !== undefined)      base.drainMult *= 1 + (s.drainMult - 1) * qm;
      if (s.damageReduction !== undefined) {
        const scaledDR = s.damageReduction * qm;
        base.damageReduction = 1 - (1 - base.damageReduction) * (1 - scaledDR);
      }
      if (s.weaponSlots !== undefined)    base.weaponSlots = s.weaponSlots;
      if (s.maxStructureBonus !== undefined) base.maxStructureBonus += s.maxStructureBonus * qm;
    }

    return base;
  }

  get weaponSlots(): number {
    return this.getComputedStats().weaponSlots;
  }

  // ─── Serialization ─────────────────────────────────────
  // Save format is score-based; any saved inventory from before the score
  // migration is dropped by the outer save-version check.

  serialize() {
    return {
      slots: { ...this.slots },
      inventory: [...this._inventory],
      weaponInventory: [...this._weaponInventory],
      equippedWeapons: [...this._equippedWeapons],
    };
  }

  deserialize(data: {
    slots: EquippedSlots;
    inventory: ScoredItem[];
    weaponInventory?: ScoredItem[];
    equippedWeapons?: (ScoredItem | null)[];
  }) {
    const isScoredItem = (v: unknown): v is ScoredItem =>
      !!v && typeof v === 'object'
      && typeof (v as ScoredItem).id === 'string'
      && typeof (v as ScoredItem).score === 'number'
      && typeof (v as ScoredItem).name === 'string';

    const sanitizeSlots = (s: unknown): EquippedSlots => {
      const empty: EquippedSlots = { engine: null, sensors: null, capacitor: null, shield: null, chassis: null };
      if (!s || typeof s !== 'object') return empty;
      const src = s as Partial<Record<keyof EquippedSlots, unknown>>;
      return {
        engine:    isScoredItem(src.engine)    ? src.engine    : null,
        sensors:   isScoredItem(src.sensors)   ? src.sensors   : null,
        capacitor: isScoredItem(src.capacitor) ? src.capacitor : null,
        shield:    isScoredItem(src.shield)    ? src.shield    : null,
        chassis:   isScoredItem(src.chassis)   ? src.chassis   : null,
      };
    };

    this.slots = sanitizeSlots(data.slots);
    const inv = Array.isArray(data.inventory) ? data.inventory : [];
    const winv = Array.isArray(data.weaponInventory) ? data.weaponInventory : [];
    const eqw = Array.isArray(data.equippedWeapons) ? data.equippedWeapons : [null];
    this._inventory = inv.filter(isScoredItem);
    this._weaponInventory = winv.filter(isScoredItem);
    this._equippedWeapons = eqw.map(v => isScoredItem(v) ? v : null);
    this.syncWeaponSlots();
  }
}
